import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { triggerDownload } from '../../services/base/BaseApi';
import { alertError } from '../../store/reducers/Alert';
import { useKeycloak } from '../../contexts/KeycloakContext';

export function buildAttachmentDownloadPath(attachmentId: string): string {
  return `Attachments/GetById?id=${attachmentId}`;
}

export const useDownload = () => {
  const dispatch = useDispatch();
  const { initialized, authenticated, login } = useKeycloak();
  const inFlightRef = useRef(new Set<string>());
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFile = useCallback(async (apiRelativePath: string, _fallbackFileName?: string) => {
    if (initialized && !authenticated) {
      login();
      return;
    }

    const path = apiRelativePath.replace(/^\//, ``);
    if (inFlightRef.current.has(path)) {
      return;
    }

    inFlightRef.current.add(path);
    setIsDownloading(true);
    try {
      await triggerDownload(apiRelativePath);
    } catch (error) {
      const message = error instanceof Error ? error.message : `Не удалось скачать файл`;
      dispatch(alertError({ summary: `Скачивание`, detail: message }));
    } finally {
      inFlightRef.current.delete(path);
      setIsDownloading(inFlightRef.current.size > 0);
    }
  }, [dispatch, initialized, authenticated, login]);

  const downloadAttachment = useCallback(async (attachmentId: string, fallbackFileName?: string) => {
    await downloadFile(buildAttachmentDownloadPath(attachmentId), fallbackFileName);
  }, [downloadFile]);

  return { downloadFile, downloadAttachment, isDownloading };
};
